import React, { useState } from 'react';
import { MessageSquare, Star, ThumbsUp, CheckCircle } from 'lucide-react';

interface FeedbackData {
  name: string;
  email: string;
  course: string;
  rating: number;
  testimonial: string;
}

export const FeedbackSection = () => {
  const [feedbackData, setFeedbackData] = useState<FeedbackData>({
    name: '',
    email: '',
    course: '',
    rating: 5,
    testimonial: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFeedbackData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFeedbackData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const feedbackFormUrl = 'https://docs.google.com/forms/d/e/1FAIpQLSdCd951aOPSt6Um7Fhmtr6YZ1WJXp2VwSCDG3RzwiPZdnr47w/formResponse';
      
      const formBody = new URLSearchParams({
        'entry.496242351': feedbackData.name,
        'entry.955038419': feedbackData.email,
        'entry.1144900135': feedbackData.course,
        'entry.1176279742': feedbackData.rating.toString(),
        'entry.1694025134': feedbackData.testimonial
      });

      await fetch(feedbackFormUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      setFeedbackSubmitted(true);
      setFeedbackData({
        name: '',
        email: '',
        course: '',
        rating: 5,
        testimonial: ''
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6 md:mb-8">
            <div className="inline-flex items-center px-3 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-2 md:mb-3" style={{borderColor: '#C4A052', color: '#6B4423'}}>
              <img 
                src="/A2ZAssist%20logo.png" 
                alt="A2Z Assist Logo" 
                className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
              />
              Share Your Experience
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4" style={{color: '#2C2420'}}>
              A2ZCampus™ Training Feedback
            </h2>
            <p className="text-sm md:text-base max-w-4xl mx-auto px-2" style={{color: '#2C2420'}}>
              Help us improve our proprietary learning tools and training programs by sharing your experience. 
              Your insights help us create better ClarityKit™, Startup Simulator™, and Real World Readiness™ experiences for future participants.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-3 md:p-5 lg:p-6 border border-gray-200">
            {feedbackSubmitted ? (
              <div className="text-center py-4 md:py-6">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full flex items-center justify-center" style={{backgroundColor: '#C4A052'}}>
                  <ThumbsUp className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-2" style={{color: '#2C2420'}}>Thank You for Your Feedback!</h3>
                <p className="text-sm md:text-base mb-3 md:mb-4 px-2" style={{color: '#2C2420'}}>
                  Your feedback helps us improve our A2ZCampus™ training programs and create better experiences for all students.
                </p>
                <button
                  onClick={() => setFeedbackSubmitted(false)}
                  className="px-5 md:px-6 py-2 md:py-2 text-white rounded-xl hover:opacity-90 transition-all font-semibold transform hover:scale-105 text-sm md:text-base"
                  style={{backgroundColor: '#C4A052'}}
                >
                  Submit Another Feedback
                </button>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className="space-y-3 md:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label htmlFor="feedback-name" className="block text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="feedback-name"
                      name="name"
                      value={feedbackData.name}
                      onChange={handleFeedbackChange}
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className="block text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="feedback-email"
                      name="email"
                      value={feedbackData.email}
                      onChange={handleFeedbackChange}
                      required
                      className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                      style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-course" className="block text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                    Training Program <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="feedback-course"
                    name="course"
                    value={feedbackData.course}
                    onChange={handleFeedbackChange}
                    required
                    className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all text-sm md:text-base"
                    style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                  >
                    <option value="">Select a program</option>
                    <option value="ClarityKit™ Workshop">ClarityKit™ Workshop</option>
                    <option value="Startup Simulator™ Experience">Startup Simulator™ Experience</option>
                    <option value="Real World Readiness™ Training">Real World Readiness™ Training</option>
                    <option value="AI Fundamentals">AI Fundamentals</option>
                    <option value="Machine Learning Basics">Machine Learning Basics</option>
                    <option value="Enterprise AI Solutions">Enterprise AI Solutions</option>
                    <option value="Campus to Career Ready Workshop">Campus to Career Ready Workshop</option>
                    <option value="Product Thinking Workshop">Product Thinking Workshop</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 md:mb-2" style={{color: '#6B4423'}}>
                    Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(star)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-6 h-6 md:w-8 md:h-8 ${
                            star <= feedbackData.rating
                              ? 'fill-current'
                              : 'text-gray-300'
                          }`}
                          style={{color: star <= feedbackData.rating ? '#C4A052' : undefined}}
                        />
                      </button>
                    ))}
                    <span className="ml-2 md:ml-3 text-sm md:text-base font-medium" style={{color: '#6B4423'}}>
                      {feedbackData.rating}/5
                    </span>
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-testimonial" className="block text-sm font-medium mb-2" style={{color: '#6B4423'}}>
                    Your Feedback <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="feedback-testimonial"
                    name="testimonial"
                    value={feedbackData.testimonial}
                    onChange={handleFeedbackChange}
                    rows={4}
                    required
                    className="w-full px-3 md:px-3 py-2 md:py-3 rounded-xl border focus:ring-2 focus:border-transparent transition-all resize-none text-sm md:text-base"
                    style={{borderColor: '#6B4423', '--tw-ring-color': '#6B4423'} as React.CSSProperties}
                    placeholder="Share your experience with the A2ZCampus™ training program. What did you learn? How will it help your career? What could be improved?"
                  ></textarea>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-6 md:px-8 py-2 md:py-3 text-white font-semibold rounded-xl shadow-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all transform hover:scale-105 text-sm md:text-base ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    style={{backgroundColor: '#C4A052', '--tw-ring-color': '#C4A052'} as React.CSSProperties}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};